import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SearchWidgetUI from '@stencil/dstories/src/components/SRP/SearchWidget/search-widget-ui';
import {
  updateSearchWidgetLocation,
  updateSearchWidgetDepartureArrival,
  updateJourneyType,
  updateSearchWidgetPassengers,
  updateSearchWidgetFlightClass,
  setSelectedFareIdentifier,
  updateSearchWidgetTravelDate,
  updateSearchWidgetAnyTime,
  updateSearchWidgetTripDuration,
  updateAnywhere
} from '@stencil/flights-seo-reducers/src/SRP/redux/actions';
import { swapSearchWidgetSoureDestinationThunk } from '@stencil/flights-seo-reducers/src/SRP/redux/thunks';
import {
  searchWidgetSelector,
  fareCalendarDataSelector,
  specialFareOptionsSelector,
  selectedFareIdentifierSelector,
  flavourSelector
} from '@stencil/flights-seo-reducers/src/SRP/redux/selectors';
import {
  getPDTDetails,
  shouldFareOptionCardBeDisplayed
} from '@stencil/flights-seo-reducers/src/utils/utils';
import getSrpUrl from '@stencil/flights-utils/src/get-srp-url';
import getDestinationPickerUrlParams from '@stencil/flights-utils/src/get-destination-picker-url-params';
import { DESTINATION_PICKER_URL } from '@stencil/flights-constants/src/urls';
import addDays from '@stencil/date-utils/src/addDays';
import { getDateFromMonth } from '@stencil/date-utils/src/getDateFromMonth';
import checkErrorSearchWidget from '@stencil/flights-utils/src/check-error-search-widget';
import {
  flightsAnalyticsLogging,
  flightsAnalyticsLoggingThunk,
  flightsSeoReducersPdtLoggingThunk
} from '@stencil/flights-seo-reducers/src/utils/common-thunks';
import { getABResponse } from '@stencil/flights-utils/src/flights-utils';

const SearchWidget = ({
  fetchFareCalendareData = null,
  language = 'english',
  isCityCountry = false
}) => {
  const dispatch = useDispatch();
  const emptyFn = () => {};

  const pokusResponse = getABResponse();
  const searchWidgetData = useSelector(searchWidgetSelector);
  const fareCalendarData = useSelector(fareCalendarDataSelector);
  const fareOptionsInfo = useSelector(specialFareOptionsSelector);
  const selectedFareIdentifier = useSelector(selectedFareIdentifierSelector);
  const flavour = useSelector(flavourSelector);

  const [displayFlightClass, setDisplayFlightClass] = useState([]);
  const [displayOnlyAdultPax, setDisplayOnlyAdultPax] = useState(false);
  const [fareOptions, setFareOptions] = useState({ ...fareOptionsInfo });

  const {
    source,
    destination,
    travelType,
    journeyType,
    departure,
    arrival,
    passengers,
    flightClass,
    travelDate,
    tripDuration,
    anyTime
  } = searchWidgetData;

  useEffect(() => {
    setFareOptions({ ...fareOptionsInfo });
  }, [fareOptionsInfo]);

  useEffect(() => {
    const fares = fareOptionsInfo?.fares || [];
    const { flightClass, passengers: paxCounts, travelType, journeyType } = searchWidgetData;
    if (fares.length) {
      let resetSelectedFareType = false;
      const faresCopy = [...fares].filter(option => {
        const showFlag = shouldFareOptionCardBeDisplayed(
          option,
          flavour,
          flightClass,
          paxCounts,
          travelType,
          journeyType
        );
        if (
          !showFlag &&
          (option.ft === selectedFareIdentifier ||
            option.identifier === selectedFareIdentifier.identifier)
        ) {
          resetSelectedFareType = true;
        }
        return showFlag;
      });
      fareOptions.fares = faresCopy;
      if (resetSelectedFareType) {
        dispatch(setSelectedFareIdentifier(''));
      }
    }
    setFareOptions({ ...fareOptions });
  }, [searchWidgetData]);

  useEffect(() => {
    if (fareOptionsInfo?.fares?.length > 0) {
      const fareOption = fareOptionsInfo.fares.find(
        fareOption => fareOption.identifier === selectedFareIdentifier
      );
      if (fareOption) {
        setDisplayFlightClass(fareOption.fare_class ? fareOption.fare_class : []);
        const { child = 0, infant = 0 } = fareOption.paxProfile;
        setDisplayOnlyAdultPax(child === 0 && infant === 0);
      } else {
        setDisplayFlightClass([]);
        setDisplayOnlyAdultPax(false);
      }
    }
  }, [selectedFareIdentifier]);

  const updateLocation = (loc, event: string) => {
    dispatch(updateSearchWidgetLocation(loc));
    if (loc && loc?.destination?.con === 'ANYWHERE') {
      dispatch(updateAnywhere(true));
    } else {
      dispatch(updateAnywhere(false));
    }
  };

  const handleSwapSourceDestination = () => {
    dispatch(swapSearchWidgetSoureDestinationThunk());
  };

  const onSearch = () => {
    if (!isCityCountry) {
      let searchParams = '';
      if (selectedFareIdentifier) {
        searchParams = `&${selectedFareIdentifier}`;
        const fareDetails = getPDTDetails(selectedFareIdentifier, fareOptionsInfo);
        const { pdt_details } = fareDetails || {};
        flightsSeoReducersPdtLoggingThunk({ ...pdt_details });
      }
      window.location.href = getSrpUrl(
        searchWidgetData,
        searchParams,
        false,
        '',
        pokusResponse?.metadataValues?.web_gi_unification,
        {
          cmpId: 'SEO',
          source: 'SEO'
        }
      );
      return;
    } else {
      const destinationPickerUrlParams = getDestinationPickerUrlParams(searchWidgetData);
      window.location.href = `${window.location.origin}${DESTINATION_PICKER_URL}?${destinationPickerUrlParams}`;
    }
  };

  const handleDateSelection = objectToDispatch => {
    dispatch(updateSearchWidgetDepartureArrival(objectToDispatch));
  };

  const handleArrivalCalendarSelection = () => {
    if (!arrival) {
      const arr = addDays(departure.dateObj, 2);
      dispatch(updateJourneyType('rt', arr));
    }
  };

  const handlePassengersUpdate = paxObj => {
    if (
      paxObj.adult !== passengers.adult ||
      paxObj.child !== passengers.child ||
      paxObj.infant !== passengers.infant
    ) {
      dispatch(updateSearchWidgetPassengers({ passengers: { ...passengers, ...paxObj } }));
    }
  };

  const handleFlightClassUpdate = flightClassObj => {
    if (flightClass.value !== flightClassObj.value) {
      dispatch(updateSearchWidgetFlightClass({ flightClass: flightClassObj }));
    }
  };

  const handleJourneyTypeUpdate = newJourneyType => {
    if (newJourneyType === 'rt') {
      const arr = addDays(departure.dateObj, 2);
      dispatch(updateJourneyType('rt', arr));
    } else {
      dispatch(updateJourneyType('ow'));
    }
  };

  const checkError = () => {
    return checkErrorSearchWidget(searchWidgetData, isCityCountry);
  };

  const handleFareOptionSelection = (fareIdentifier, fareOption) => {
    const { omniture_details = {}, pdt_details = {} } = fareOption || {};

    let action_name = '';
    let omnitureEventName = '';
    let category = 'SRP_Search_Widget';
    if (selectedFareIdentifier === fareIdentifier) {
      dispatch(setSelectedFareIdentifier(''));
      action_name = pdt_details?.special_fare_deselected;
      omnitureEventName = omniture_details?.special_fare_deselected;
    } else {
      dispatch(setSelectedFareIdentifier(fareIdentifier));
      action_name = pdt_details?.special_fare_selected;
      omnitureEventName = omniture_details?.special_fare_selected;
    }
    dispatch(
      flightsAnalyticsLoggingThunk({
        pdtInfo: {
          action_name,
          meta_data: { category }
        },
        omnitureInfo: {
          pageType: 'SEO',
          eventName: omnitureEventName
        }
      })
    );
  };

  const handleAnyTime = () => {
    dispatch(updateSearchWidgetAnyTime(true));
    dispatch(updateSearchWidgetTravelDate(''));
  };

  const handleTravelDate = travelDateMonth => {
    const travelDate = getDateFromMonth(travelDateMonth);
    dispatch(updateSearchWidgetTravelDate(travelDate));
    dispatch(updateSearchWidgetAnyTime(false));
  };

  const handleTripDuration = tripDuration => {
    dispatch(updateSearchWidgetTripDuration(tripDuration));
  };

  return (
    <SearchWidgetUI
      source={source}
      destination={destination}
      travelType={travelType}
      journeyType={journeyType}
      departure={departure}
      arrival={arrival}
      passengers={passengers}
      flightClass={flightClass}
      fareOptionsInfo={fareOptions}
      handleFareOptionSelection={handleFareOptionSelection}
      selectedFareIdentifier={selectedFareIdentifier}
      gaHandler={emptyFn}
      handleDateSelection={handleDateSelection}
      handleArrivalCalendarSelection={handleArrivalCalendarSelection}
      updateLocation={updateLocation}
      updateSearch={onSearch}
      checkError={checkError}
      handlePassengersUpdate={handlePassengersUpdate}
      handleFlightClassUpdate={handleFlightClassUpdate}
      handleJourneyTypeUpdate={handleJourneyTypeUpdate}
      handleSwapSourceDestination={handleSwapSourceDestination}
      isSEO={true}
      getFareData={fetchFareCalendareData}
      fareCalendarData={fareCalendarData}
      language={language}
      displayFlightClass={displayFlightClass}
      displayOnlyAdultPax={displayOnlyAdultPax}
      // city country params
      isCityCountry={isCityCountry}
      isAnyTimeSelected={anyTime}
      travelDate={travelDate}
      tripDuration={tripDuration}
      handleAnyTime={handleAnyTime}
      handleTripDuration={handleTripDuration}
      handleTravelDate={handleTravelDate}
    />
  );
};

export default SearchWidget;
